import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { clientApi } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { format } from 'date-fns';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';

const v = (n) => n ?? 0;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label || payload[0]?.name}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.payload?.fill || p.color }} className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ background: p.payload?.fill || p.color }} />
          {p.name || p.payload?.name}: <span className="font-bold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const STATUS_CARDS = (s) => [
  {
    label: 'Total',
    sublabel: 'All requests',
    value: v(s.total),
    from: '#1e40af', to: '#3b82f6',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: 'Account Review',
    sublabel: 'Awaiting your review',
    value: v(s.pending),
    from: '#0891b2', to: '#22d3ee',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    urgent: true,
  },
  {
    label: 'Assigned to Vendor',
    sublabel: 'Sent to vendor',
    value: v(s.assignedToVendor),
    from: '#7c3aed', to: '#a78bfa',
    navigateStatus: 'ASSIGNED',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: 'Awaiting OmniDrive Review',
    sublabel: 'Vendor submitted BOM',
    value: v(s.awaitingOmniDriveReview),
    from: '#6366f1', to: '#818cf8',
    navigateStatus: 'VENDOR_SUBMITTED',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: 'Awaiting CVA',
    sublabel: 'Pending CVA final approval',
    value: v(s.awaitingCva),
    from: '#d97706', to: '#f59e0b',
    navigateStatus: 'PENDING_FINAL_APPROVAL',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: 'Fully Approved',
    sublabel: 'CVA approved · Complete',
    value: v(s.approved),
    from: '#059669', to: '#10b981',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: 'Rejected',
    sublabel: 'Sent back for revision',
    value: v(s.rejected),
    navigateStatus: 'REJECTED',
    from: '#dc2626', to: '#f87171',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    label: 'Cancelled',
    sublabel: 'Process permanently cancelled',
    value: v(s.cancelled),
    from: '#6b7280', to: '#9ca3af',
    navigateStatus: 'CANCELLED',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

export default function ClientDashboard() {
  const navigate = useNavigate();

  const { data: pendingData, isLoading } = useQuery(
    'client-pending',
    () => clientApi.getFinishedGoods({ status: 'COMPANY_APPROVED', size: 5 }),
    { refetchInterval: 60000 }
  );

  // Aggregated server-side counts — accurate even when the company has
  // thousands of finished goods (previously computed client-side from a
  // page capped at 1000 rows, which undercounted larger accounts).
  const { data: statsData } = useQuery(
    'client-stats',
    () => clientApi.getDashboardStats(),
    { refetchInterval: 60000 }
  );
  const { data: trendData } = useQuery(
    'client-trend',
    () => clientApi.getActivityTrend({ months: 6 })
  );

  const pendingItems = pendingData?.data?.content || [];
  const rawStats     = statsData?.data ?? statsData;
  const rawTrend      = trendData?.data ?? trendData;
  const trendPoints   = Array.isArray(rawTrend) ? rawTrend : [];

  const stats = useMemo(() => ({
    total:               rawStats?.totalRequests ?? 0,
    pending:             rawStats?.companyApproved ?? 0,
    assignedToVendor:    rawStats?.assignedRequests ?? 0,
    awaitingOmniDriveReview: rawStats?.vendorSubmitted ?? 0,
    awaitingCva:         rawStats?.pendingFinalApproval ?? 0,
    approved:            rawStats?.clientApproved ?? 0,
    rejected:            rawStats?.rejected ?? 0,
    inProcess:           (rawStats?.assignedRequests ?? 0) + (rawStats?.vendorSubmitted ?? 0),
    cancelled:           rawStats?.cancelled ?? 0,
  }), [rawStats]);

  const pieData = useMemo(() => [
    { name: 'Account Review', value: stats.pending, fill: '#0891b2' },
    { name: 'In Process', value: stats.inProcess, fill: '#3b82f6' },
    { name: 'Approved',   value: stats.approved,  fill: '#10b981' },
    { name: 'Rejected',   value: stats.rejected,  fill: '#ef4444' },
    { name: 'Cancelled',  value: stats.cancelled, fill: '#9ca3af' },
  ].filter(d => d.value > 0), [stats]);

  // Server-aggregated monthly trend (mirrors the platform dashboard's trend chart fields).
  const barData = useMemo(() => trendPoints.map(pt => ({
    month:        pt.month,
    'In Process': Number(pt.inProcess ?? 0),
    'Awaiting CVA': Number(pt.pendingFinalApproval ?? 0),
    Approved:     Number(pt.approved ?? 0),
    Rejected:     Number(pt.rejected ?? 0),
    Cancelled:    Number(pt.cancelled ?? 0),
  })), [trendPoints]);

  if (isLoading) return <LoadingSpinner message="Loading your dashboard..." />;

  return (
    <div className="space-y-6 pb-8">

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Account Dashboard</h1>
          <p className="text-gray-400 text-sm mt-0.5">Review and approve vendor submissions</p>
        </div>
        {stats.pending > 0 && (
          <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-600 border border-amber-200">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
            {stats.pending} need review
          </span>
        )}
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-8 gap-4">
        {STATUS_CARDS(stats).map((card) => (
          <div
            key={card.label}
            onClick={() => card.navigateStatus && card.value > 0 && navigate('/client/requests', { state: { status: card.navigateStatus } })}
            className={`relative overflow-hidden rounded-2xl p-5 transition-transform hover:-translate-y-0.5 hover:shadow-lg ${card.navigateStatus && card.value > 0 ? 'cursor-pointer' : ''}`}
            style={{ background: `linear-gradient(135deg, ${card.from}, ${card.to})` }}
          >
            <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-white opacity-10" />
            <div className="absolute -right-1 -bottom-6 h-24 w-24 rounded-full bg-white opacity-10" />
            <div className="absolute top-3 right-3 bg-white bg-opacity-20 rounded-xl p-2">{card.icon}</div>
            <div className="relative pr-9">
              <p className="text-white text-opacity-80 text-xs font-medium uppercase tracking-wide leading-tight">{card.label}</p>
              <p className="text-white text-4xl font-black mt-1 leading-none">{card.value}</p>
              <p className="text-white text-opacity-70 text-xs mt-1.5">{card.sublabel}</p>
            </div>
            {card.urgent && card.value > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); navigate('/client/requests', { state: { status: 'COMPANY_APPROVED' } }); }}
                className="relative mt-3 text-white text-opacity-90 text-xs font-semibold underline underline-offset-2"
              >
                Review now →
              </button>
            )}
            {card.navigateStatus && card.value > 0 && !card.urgent && (
              <p className="relative mt-3 text-white text-opacity-90 text-xs font-semibold underline underline-offset-2">
                View requests →
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">

        {/* Pie chart */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="mb-5">
            <h2 className="text-sm font-bold text-gray-900">Request Breakdown</h2>
            <p className="text-xs text-gray-400 mt-0.5">Distribution across all statuses</p>
          </div>
          {pieData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-300 text-sm">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                  paddingAngle={3} dataKey="value">
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} stroke="none" />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Bar chart */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="mb-5">
            <h2 className="text-sm font-bold text-gray-900">Monthly Trend</h2>
            <p className="text-xs text-gray-400 mt-0.5">6-month review activity</p>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={barData} barSize={18} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb' }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Bar dataKey="Approved"     fill="#10b981" radius={[4,4,0,0]} />
              <Bar dataKey="In Process"   fill="#7c3aed" radius={[4,4,0,0]} />
              <Bar dataKey="Awaiting CVA" fill="#f59e0b" radius={[4,4,0,0]} />
              <Bar dataKey="Rejected"     fill="#ef4444" radius={[4,4,0,0]} />
              <Bar dataKey="Cancelled"    fill="#9ca3af" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Pending Review Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-gray-900 text-sm">Awaiting Your Review</h2>
            <p className="text-xs text-gray-400 mt-0.5">Items ready for your approval</p>
          </div>
          {pendingItems.length > 0 && (
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
              {pendingItems.length} pending
            </span>
          )}
        </div>

        {pendingItems.length === 0 ? (
          <div className="py-14 text-center">
            <div className="text-5xl mb-3">✅</div>
            <p className="font-semibold text-gray-700">All caught up!</p>
            <p className="text-sm text-gray-400 mt-1">No submissions awaiting your review</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {pendingItems.map((fg) => (
              <div key={fg.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <span className="text-amber-600 text-sm font-bold">
                      {fg.fgName?.charAt(0) || 'F'}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{fg.fgName}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      <span className="font-mono text-blue-500">{fg.fgNumber}</span>
                      {fg.updatedAt && ` · ${format(new Date(fg.updatedAt), 'dd MMM yyyy')}`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/client/review/${fg.id}`)}
                  className="px-4 py-1.5 text-sm font-semibold rounded-xl border transition-all flex-shrink-0"
                  style={{
                    background: 'linear-gradient(135deg,#d97706,#f59e0b)',
                    color: 'white',
                    border: 'none',
                  }}
                >
                  Review →
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="px-6 py-3 border-t border-gray-50 flex justify-end">
          <button onClick={() => navigate('/client/requests')}
            className="text-xs text-blue-500 hover:text-blue-700 font-semibold transition-colors">
            View all requests →
          </button>
        </div>
      </div>
    </div>
  );
}
